import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer, EntityList } from '@aimsos/ui';

import { buildActivityCardView } from '../../utils/activityCardViews.js';

/**
 * One email, SMS or note in the Activity feed (CT-5527, CT-5528).
 *
 * `CartContainer` + `EntityList`, the same pair every list in this domain uses —
 * not `ActivityListWidget` (a dashboard widget), not a hand-built card. The item
 * maps onto `EntityList`'s own slots:
 *
 *   subject            -> title
 *   direction          -> primaryMeta (arrow + Inbound / Outbound)
 *   status             -> status badge
 *   occurredAt         -> timestamp
 *   body preview       -> description
 *   AI summary         -> aiInsight, with the component's built-in expand
 *   sender, recipients -> secondaryMeta
 *   channel            -> tags
 *
 * An email or SMS has no row action in v2.0 — the email preview, thread and
 * reply are out — so its card is not focusable and renders no eye.
 *
 * A NOTE is the other variant (CT-5528), on the same card — not a second
 * component: the scope rides the status badge, the author sits in the footer,
 * the short summary is the AI line, and the eye opens the preview through
 * `onPreview(item)`. `onPreview` is the page's one stable handler; the card
 * hands back its own item.
 *
 * History (CT-5724) adds three variants on the SAME card, not a second family:
 * a TASK (status, assignee, due date, context, where it came from), a CALL record
 * (direction, record type, AI summary, phone, duration, agent tools, tone) and a
 * call or MEETING logged by hand (what was discussed, duration, participants).
 * `highlighted` marks the event a History link named and scrolls it into view.
 *
 * What each kind shows is `buildActivityCardView`'s (`utils/activityCardViews.js`:
 * one pure builder per kind, one map). The view is built in one `useMemo`, so a
 * re-render that changes nothing about the item hands `EntityList` the same
 * objects and it does not repaint.
 */
const ActivityCard = memo(function ActivityCard({ item, highlighted = false, onPreview }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;
  const ref = useRef(null);

  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlighted]);

  const handlePreview = useCallback(() => onPreview?.(item), [onPreview, item]);

  // What each KIND shows lives in `buildActivityCardView` (one builder per kind);
  // the card only renders it.
  const view = useMemo(
    () => buildActivityCardView(item, { t, language, onPreview: onPreview ? handlePreview : undefined }),
    [item, language, t, onPreview, handlePreview],
  );

  return (
    <div
      ref={ref}
      data-testid="activity-card"
      data-activity-id={item.id}
      data-activity-kind={item.kind}
      data-highlighted={highlighted ? 'true' : undefined}
    >
      <CartContainer size="S" variant="neutral" selected={highlighted}>
        <EntityList
          {...view}
          aiInsightExpandAriaLabel={t('activity.card.expandSummary')}
          aiInsightCollapseAriaLabel={t('activity.card.collapseSummary')}
        />
      </CartContainer>
    </div>
  );
});

export default ActivityCard;
